<template>
    <div class="youtube-panel" :class="{ active: isActive }">
      <div v-if="currentVideo" class="video-container">
        <iframe
          :src="embedUrl"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          class="youtube-iframe">
        </iframe>

        <div class="video-controls">
          <div class="video-info">
            <h3 class="video-title">{{ videoTitle }}</h3>
            <p class="video-source">{{ videoSource }}</p>
          </div>

          <button class="close-button" @click="closeVideo">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="youtubeVideos.length > 0 && !currentVideo" class="video-list">
        <h3 class="list-title">YouTube Videos</h3>
        <div class="video-thumbnails">
          <div
            v-for="(video, index) in youtubeVideos"
            :key="index"
            class="video-thumbnail"
            @click="playVideo(video)">
            <div class="thumbnail-container">
              <img :src="getYouTubeThumbnail(video.url)" alt="Video thumbnail" />
              <div class="play-overlay">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            </div>
            <p class="thumbnail-title">{{ extractVideoTitle(video) }}</p>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script>
  export default {
    name: 'YouTubePanel',
    props: {
      searchResults: {
        type: Array,
        default: () => []
      },
      isActive: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        currentVideo: null
      }
    },
    computed: {
      youtubeVideos() {
        if (!this.searchResults) return [];
        return this.searchResults.filter(result => {
          return this.isYouTubeUrl(result.url);
        });
      },
      embedUrl() {
        if (!this.currentVideo) return '';

        // Extract YouTube video ID
        const videoId = this.extractVideoId(this.currentVideo.url);
        if (!videoId) return '';

        // Create embed URL with autoplay
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      },
      videoTitle() {
        if (!this.currentVideo) return '';
        return this.extractVideoTitle(this.currentVideo);
      },
      videoSource() {
        if (!this.currentVideo) return '';
        return this.currentVideo.title || 'YouTube Video';
      }
    },
    methods: {
      isYouTubeUrl(url) {
        if (!url) return false;

        // Check various YouTube URL formats
        return url.includes('youtube.com') ||
               url.includes('youtu.be') ||
               url.includes('youtube-nocookie.com');
      },

      extractVideoId(url) {
        if (!url) return null;

        // Handle standard YouTube URLs - FIXED REGEX to avoid escaped forward slashes
        let match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);

        return match ? match[1] : null;
      },

      extractVideoTitle(video) {
        // Try to extract title from search result
        if (video.title) return video.title;

        // Use snippet if available
        if (video.snippet) {
          // Clean up snippet to make it more title-like
          return video.snippet
            .split(' - YouTube')[0]
            .split(' | YouTube')[0]
            .split(' on YouTube')[0]
            .trim();
        }

        // Fallback to URL
        return 'YouTube Video';
      },

      getYouTubeThumbnail(url) {
        const videoId = this.extractVideoId(url);
        if (!videoId) return '';

        // Return high quality thumbnail URL
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      },

      playVideo(video) {
        this.currentVideo = video;
        this.$emit('video-started', video);
      },

      closeVideo() {
        this.currentVideo = null;
        this.$emit('video-closed');
      }
    }
  }
  </script>

  <style scoped>
  .youtube-panel {
    display: none;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
  }

  .theme--dark .youtube-panel {
    background-color: #1a1a1a;
  }

  .theme-light .youtube-panel {
    background-color: #f5f5f5;
  }

  .youtube-panel.active {
    display: block;
  }

  .video-container {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 Aspect Ratio */
    background-color: #000;
  }

  .youtube-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  .video-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #212121;
    color: #fff;
  }

  .video-info {
    max-width: 85%;
  }

  .video-title {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .video-source {
    margin: 0;
    font-size: 14px;
    color: #aaa;
  }

  .close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .close-button:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .video-list {
    padding: 16px;
  }

  .list-title {
    margin: 0 0 12px 0;
    font-size: 16px;
    font-weight: 500;
    color: #fff;
  }

  .video-thumbnails {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }

  .video-thumbnail {
    cursor: pointer;
    transition: transform 0.2s;
  }

  .video-thumbnail:hover {
    transform: translateY(-2px);
  }

  .thumbnail-container {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 Aspect Ratio */
    margin-bottom: 8px;
    border-radius: 6px;
    overflow: hidden;
    background-color: #000;
  }

  .thumbnail-container img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .play-overlay svg {
    color: white;
    width: 48px;
    height: 48px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  }

  .thumbnail-container:hover .play-overlay {
    opacity: 1;
  }

  .thumbnail-title {
    margin: 0;
    font-size: 14px;
    color: #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .video-thumbnails {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 12px;
    }

    .video-title {
      font-size: 14px;
    }

    .video-source {
      font-size: 12px;
    }
  }

  /* Light theme overrides */
  :global(.theme--light) .youtube-panel {
    background-color: #f5f5f5;
  }

  :global(.theme--light) .video-controls {
    background-color: #e5e5e5;
    color: #333;
  }

  :global(.theme--light) .video-source {
    color: #666;
  }

  :global(.theme--light) .list-title {
    color: #333;
  }

  :global(.theme--light) .thumbnail-title {
    color: #333;
  }

  :global(.theme--light) .close-button {
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
  }

  :global(.theme--light) .close-button:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
  </style>
